import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Events } from 'src/providers/events/events';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { Vendors } from 'src/providers/vendors/vendors';
import { Util } from 'src/shared/util/util';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

import {
  CUSTOMER_VENDORS_CHANGED_EVENT,
  Vendor,
  VendorsPage,
} from 'src/models/vendor.model';

import { EditVendorPage } from 'src/pages/my-pros/edit-vendor/edit-vendor';
import { VendorDetailPage } from 'src/pages/my-pros/vendor-detail/vendor-detail';

const PER_PAGE = 25;

@Component({
  selector: 'vendors-tab',
  templateUrl: './vendors-tab.component.html',
  styleUrls: ['./vendors-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VendorsTabComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly events = inject(Events);
  private readonly vendors = inject(Vendors);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  readonly windowService = inject(WindowService);

  readonly headers = ['Name', 'Category', 'Email', 'Phone', 'last-column'];
  readonly searchForm: UntypedFormGroup;

  rows: any[] = [];
  allVendors: Vendor[] = [];
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  perPage = PER_PAGE;
  loaded = false;
  isFetching = false;
  errorMessage = '';

  private changedSubscription?: Subscription;
  private lastSearch = '';

  boundGoToAddVendor = () => this.openEditVendor('create');

  constructor() {
    this.searchForm = this.fb.group({ search: [''] });
    this.searchForm.get('search')!.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => this.onSearchChange(value ?? ''));
  }

  async ngOnInit(): Promise<void> {
    this.changedSubscription = this.events.subscribe(CUSTOMER_VENDORS_CHANGED_EVENT, () => this.fetch());
    await this.fetch();
  }

  ngOnDestroy(): void {
    this.changedSubscription?.unsubscribe();
  }

  hasResults(): boolean {
    return this.loaded && this.allVendors.length > 0;
  }

  isEmpty(): boolean {
    return this.loaded && this.allVendors.length === 0 && !this.lastSearch;
  }

  isEmptySearch(): boolean {
    return this.loaded && this.allVendors.length === 0 && !!this.lastSearch;
  }

  rangeStart(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
  }

  rangeEnd(): number {
    return Math.min(this.currentPage * this.perPage, this.totalCount);
  }

  canGoPrev(): boolean { return this.currentPage > 1 && !this.isFetching; }
  canGoNext(): boolean { return this.currentPage < this.totalPages && !this.isFetching; }

  prevPage = () => { if (this.canGoPrev()) { this.currentPage -= 1; this.fetch(); } };
  nextPage = () => { if (this.canGoNext()) { this.currentPage += 1; this.fetch(); } };

  goToAddVendor() { this.openEditVendor('create'); }
  goToVendorDetail(vendor: Vendor) {
    this.openPanel(`vendor-detail/${vendor.id}`, { vendorId: vendor.id, vendor }, VendorDetailPage);
  }

  onTableCellClick(event: { column: string; element: any }) {
    const vendor: Vendor | undefined = event.element?.vendor;
    if (!vendor) { return; }
    if (event.column === 'last-column') {
      this.confirmDelete(vendor);
      return;
    }
    this.goToVendorDetail(vendor);
  }

  async confirmDelete(vendor: Vendor) {
    const translate = new TranslationPipe();
    const alert = await this.util.showConfirmAlert({
      header: translate.transform('Remove Vendor'),
      message: translate.transform(
        vendor.name
          ? `Are you sure you want to remove ${vendor.name}?`
          : 'Are you sure you want to remove this vendor?'
      ),
      buttons: [
        { text: translate.transform('Go Back'), role: 'cancel', cssClass: 'secondary' },
        { text: translate.transform('Remove Vendor'), role: 'confirm' },
      ],
    });
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') { return; }
    await this.deleteVendor(vendor);
  }

  private async deleteVendor(vendor: Vendor) {
    try {
      await firstValueFrom(this.vendors.deleteVendor(vendor.id));
      this.util.showSuccess('Vendor removed');
      await this.fetch();
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Could not remove vendor');
    }
  }

  private async openEditVendor(mode: 'create' | 'edit', vendor?: Vendor) {
    const params = { mode, vendor: vendor ?? null };
    if (this.windowService.isDesktopRes) {
      const isPanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      if (isPanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo('edit-vendor', params, EditVendorPage);
      } else {
        this.rightSidePanelService.openRightSidePanel(EditVendorPage);
      }
      return;
    }
    await this.navCtrl.navigateForward('edit-vendor', params);
  }

  private async openPanel(url: string, params: any, component: any) {
    if (this.windowService.isDesktopRes) {
      const isPanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      if (isPanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.rightSidePanelService.openRightSidePanel(component);
      }
      return;
    }
    await this.navCtrl.navigateForward(url, params);
  }

  private onSearchChange(value: string) {
    const trimmed = value.trim();
    if (trimmed === this.lastSearch) { return; }
    this.lastSearch = trimmed;
    this.currentPage = 1;
    this.fetch();
  }

  private async fetch() {
    this.isFetching = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      const page = await firstValueFrom(this.vendors.listVendors({
        page: this.currentPage,
        perPage: this.perPage,
        search: this.lastSearch || undefined,
      }));
      this.applyPage(page);
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load vendors';
    } finally {
      this.isFetching = false;
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  private applyPage(page: VendorsPage) {
    this.allVendors = page.vendors;
    this.totalCount = page.totalCount;
    this.totalPages = page.totalPages;
    this.currentPage = page.currentPage || this.currentPage;
    this.perPage = page.perPage || this.perPage;
    this.rows = page.vendors.map(v => this.toRow(v));
  }

  private toRow(vendor: Vendor): any {
    return {
      vendor,
      Name: { value: vendor.name || '—', initials: true },
      Category: { value: vendor.vendor_category?.name || '' },
      Email: { value: vendor.email || '' },
      Phone: { value: vendor.phone || '' },
      'last-column': { value: '', action: null, icon: 'assets/svg/trash-outline.svg' },
    };
  }
}
